import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Add, ContentCopy, Done } from "@mui/icons-material";
import { useApiClient } from "../../../api";
import { FruitifyContainer } from "../../../shared-components/Containers";

function SecretRevealBox({ secret }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(secret);
    setCopied(true);
  };

  return (
    <Box
      sx={{
        mt: 2,
        p: 2,
        bgcolor: "warning.light",
        borderRadius: 1,
        border: "1px solid",
        borderColor: "warning.main",
      }}
    >
      <Typography variant="body2" fontWeight="bold" gutterBottom>
        Secret Token — copy now, it will not be shown again
      </Typography>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Typography
          variant="body2"
          sx={{
            fontFamily: "monospace",
            wordBreak: "break-all",
            flex: 1,
          }}
        >
          {secret}
        </Typography>
        <Tooltip title={copied ? "Copied!" : "Copy to clipboard"}>
          <IconButton
            size="small"
            onClick={handleCopy}
            color={copied ? "success" : "default"}
          >
            {copied ? (
              <Done fontSize="small" />
            ) : (
              <ContentCopy fontSize="small" />
            )}
          </IconButton>
        </Tooltip>
      </Stack>
    </Box>
  );
}

function GenerateKeyDialog({ open, onClose, onGenerated }) {
  const ApiClient = useApiClient();
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async () => {
    if (!name.trim()) {
      setError("Name is required");
      return;
    }
    setLoading(true);
    try {
      const result = await ApiClient.apiAccess.generateApiKey({
        name: name.trim(),
        scopes: ["training_data:read", "job_metrics:read"], // TODO: For now just display static scope
      });
      onGenerated(result);
      setName("");
      setError("");
    } catch (e) {
      setError("Failed to generate key. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setName("");
    setError("");
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Generate New API Key</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          fullWidth
          label="Key name"
          placeholder="e.g. fruitify-classifier-prod"
          value={name}
          onChange={(e) => setName(e.target.value)}
          error={!!error}
          helperText={error || "A descriptive label for this key"}
          sx={{ mt: 1 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} /> : <Add />}
        >
          Generate
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function APIKeyCard({ apiKey, onDeactivated }) {
  const ApiClient = useApiClient();
  const [deactivating, setDeactivating] = useState(false);

  const handleDeactivate = async () => {
    setDeactivating(true);
    try {
      await ApiClient.apiAccess.deactivateApiKey({ id: apiKey.id });
      onDeactivated(apiKey.id);
    } catch (e) {
      console.error("Failed to deactivate key", e);
    } finally {
      setDeactivating(false);
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        spacing={1}
      >
        <Box>
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            flexWrap="wrap"
          >
            <Typography variant="subtitle1" fontWeight="bold">
              {apiKey.name}
            </Typography>
            <Chip
              label={apiKey.isActive ? "Active" : "Inactive"}
              color={apiKey.isActive ? "success" : "default"}
              size="small"
            />
          </Stack>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontFamily: "monospace", mt: 0.5 }}
          >
            {apiKey.key}
          </Typography>
          <Stack direction="row" spacing={0.5} mt={0.5} flexWrap="wrap">
            {(apiKey.scopes || []).map((scope) => (
              <Chip key={scope} label={scope} size="small" variant="outlined" />
            ))}
          </Stack>
          <Typography
            variant="caption"
            color="text.secondary"
            display="block"
            mt={0.5}
          >
            Created {new Date(apiKey.createdAt).toLocaleDateString()}
            {apiKey.lastUsedAt &&
              ` · Last used ${new Date(apiKey.lastUsedAt).toLocaleDateString()}`}
          </Typography>
        </Box>

        {apiKey.isActive && (
          <Button
            size="small"
            variant="outlined"
            color="error"
            onClick={handleDeactivate}
            disabled={deactivating}
          >
            {deactivating ? <CircularProgress size={14} /> : "Deactivate"}
          </Button>
        )}
      </Stack>
    </Paper>
  );
}

export default function ApiAccessPage() {
  const ApiClient = useApiClient();
  const [apiKeys, setApiKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generateOpen, setGenerateOpen] = useState(false);
  const [pendingSecret, setPendingSecret] = useState(null);

  const fetchKeys = async () => {
    setLoading(true);
    try {
      const keys = await ApiClient.apiAccess.getApiKeys();
      setApiKeys(keys || []);
    } catch (e) {
      console.error("Failed to fetch API keys", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchKeys();
  }, []);

  const handleGenerated = async ({ apiKey, secret }) => {
    setApiKeys((prev) => [apiKey, ...prev]);
    setPendingSecret(secret);
    setGenerateOpen(false);
  };

  const handleDeactivated = (id) => {
    setApiKeys((prev) =>
      prev.map((k) => (k.id === id ? { ...k, isActive: false } : k)),
    );
  };

  const activeKeys = apiKeys.filter((k) => k.isActive);
  const inactiveKeys = apiKeys.filter((k) => !k.isActive);

  return (
    <FruitifyContainer>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        mb={3}
      >
        <Box>
          <Typography variant="h5" fontWeight="bold">
            API Access
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage API keys for machine-to-machine access. Secret tokens are
            shown once on generation.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => setGenerateOpen(true)}
          sx={{ mt: { xs: 2, sm: 0 } }}
        >
          Generate Key
        </Button>
      </Stack>

      {pendingSecret && (
        <Box mb={3}>
          <SecretRevealBox secret={pendingSecret} />
        </Box>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" mt={4}>
          <CircularProgress />
        </Box>
      ) : (
        <Stack spacing={2}>
          {activeKeys.length === 0 && inactiveKeys.length === 0 && (
            <Typography color="text.secondary" textAlign="center" mt={4}>
              No API keys yet. Generate one to get started.
            </Typography>
          )}

          {activeKeys.map((apiKey) => (
            <APIKeyCard
              key={apiKey.id}
              apiKey={apiKey}
              onDeactivated={handleDeactivated}
            />
          ))}

          {inactiveKeys.length > 0 && (
            <>
              <Divider sx={{ my: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  Inactive
                </Typography>
              </Divider>
              {inactiveKeys.map((apiKey) => (
                <APIKeyCard
                  key={apiKey.id}
                  apiKey={apiKey}
                  onDeactivated={handleDeactivated}
                />
              ))}
            </>
          )}
        </Stack>
      )}

      <GenerateKeyDialog
        open={generateOpen}
        onClose={() => setGenerateOpen(false)}
        onGenerated={handleGenerated}
      />
    </FruitifyContainer>
  );
}
